// Featured looks rail for the Style section. Visually matches the Aura
// "Top creators" rail: 124×180 portrait cards, rank badge top-left, heart
// chip top-right, name + template label over a bottom gradient.
//
// Scroll: natively swipeable left/right (works on Android touch) AND
// auto-drifts to the right by incrementing scrollLeft. Pauses briefly when
// the user touches or hovers, resumes after a short idle window.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSignedImage } from "@/hooks/useSignedImage";
import type { FeaturedItem } from "@/hooks/useFeaturedSubmissions";
import { Heart, Flame, ChevronRight } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import { GetTheAppInlineCard } from "@/components/GetTheAppInlineCard";

interface Props {
  items: FeaturedItem[];
  /** Kept for API compatibility — no longer used. */
  onReuse?: (item: FeaturedItem) => void;
  /** Auto-scroll speed in px/sec. Default 35 px/s (slower than Scenes). */
  pxPerSec?: number;
}

const RailTile = ({
  item,
  rank,
  onClick,
}: {
  item: FeaturedItem;
  rank: number;
  onClick: () => void;
}) => {
  const url = useSignedImage("featured-images", item.permanent_image_path, {
    width: 320,
    height: 460,
    resize: "cover",
    quality: 70,
  });
  const title = item.caption ?? item.template_label ?? "Featured look";
  const sub = item.caption && item.template_label ? item.template_label : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-[180px] w-[124px] shrink-0 snap-start overflow-hidden rounded-2xl bg-black ring-1 ring-white/10 transition-transform active:scale-95"
    >
      {url ? (
        <img
          src={url}
          alt={title}
          className="h-full w-full object-cover"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          draggable={false}
        />
      ) : (
        <div className="h-full w-full animate-pulse bg-muted" />
      )}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

      <div className="absolute left-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-black/65 px-1.5 text-[10px] font-bold text-white ring-1 ring-white/20 backdrop-blur">
        #{rank}
      </div>
      <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/65 px-1.5 py-0.5 text-[10px] font-bold text-white ring-1 ring-white/20 backdrop-blur">
        <Heart className="h-2.5 w-2.5" />
        <span className="tabular-nums">1</span>
      </div>

      <div className="absolute inset-x-0 bottom-0 px-2 pb-2 text-left text-white">
        <p className="truncate text-[11px] font-bold drop-shadow">{title}</p>
        {sub && (
          <p className="truncate text-[10px] font-medium opacity-90 drop-shadow">{sub}</p>
        )}
      </div>
    </button>
  );
};

export const FeaturedStrip = ({ items, pxPerSec = 35 }: Props) => {
  const navigate = useNavigate();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const firstHalfRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const pausedUntilRef = useRef<number>(0);
  const posRef = useRef<number>(0);
  const userScrollRef = useRef<boolean>(false);

  const web = !isNativeApp();
  const visibleItems = web ? items.slice(0, 3) : items;

  useEffect(() => {
    const el = scrollerRef.current;
    const firstHalf = firstHalfRef.current;
    if (!el || !firstHalf || visibleItems.length === 0) return;

    posRef.current = el.scrollLeft;
    const onScroll = () => {
      if (userScrollRef.current) posRef.current = el.scrollLeft;
    };
    el.addEventListener("scroll", onScroll, { passive: true });

    const GAP = 10;
    const step = (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      if (ts >= pausedUntilRef.current) {
        const loop = firstHalf.offsetWidth + GAP;
        posRef.current += pxPerSec * dt;
        if (loop > 0 && posRef.current >= loop) posRef.current -= loop;
        el.scrollLeft = posRef.current;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [visibleItems.length, pxPerSec]);

  const pauseFor = (ms: number) => {
    userScrollRef.current = true;
    pausedUntilRef.current = performance.now() + ms;
    window.setTimeout(() => { userScrollRef.current = false; }, ms + 50);
  };

  if (items.length === 0) return null;

  const openInFeed = (it: FeaturedItem) => {
    navigate("/app/aura", { state: { focusFeaturedId: it.id } });
  };

  return (
    <section className="relative" aria-label="Featured looks">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <Flame className="h-3.5 w-3.5 text-amber-500" /> Featured
        </h2>
        <button
          type="button"
          onClick={() => navigate("/app/aura")}
          className="flex items-center gap-0.5 text-[11px] font-bold text-primary active:scale-95"
        >
          See all <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={scrollerRef}
        className={cn(
          "-mx-5 flex h-[180px] w-[calc(100%+2.5rem)] gap-[10px] overflow-x-auto overflow-y-hidden scrollbar-none",
        )}
        style={{
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onTouchStart={() => pauseFor(2500)}
        onPointerDown={() => pauseFor(2500)}
        onWheel={() => pauseFor(2500)}
        onMouseEnter={() => pauseFor(60_000)}
        onMouseLeave={() => pauseFor(0)}
      >
        <div ref={firstHalfRef} className="flex shrink-0 gap-[10px]">
          {visibleItems.map((it, i) => (
            <RailTile key={`a-${it.id}`} item={it} rank={i + 1} onClick={() => openInFeed(it)} />
          ))}
          {web && <GetTheAppInlineCard variant="rail-tile" />}
        </div>
        <div className="flex shrink-0 gap-[10px]">
          {visibleItems.map((it, i) => (
            <RailTile key={`b-${it.id}`} item={it} rank={i + 1} onClick={() => openInFeed(it)} />
          ))}
          {web && <GetTheAppInlineCard variant="rail-tile" />}
        </div>
      </div>
    </section>
  );
};
